import {AfterViewInit, ChangeDetectorRef, Component, inject, model, OnInit, signal, ViewChild} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent, DateOptions,
  FormComponent,
  ModalComponent,
  ModalOptions
} from "@shared/components";
import {DropdownComponent, DropdownOptions} from "@shared/components/dropdown/dropdown.component";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {NgOptimizedImage} from "@angular/common";
import {assets} from '@core/constants';
import {Coding} from '@core/classes/other/coding';
import {APIService, DataService, ToastService} from '@core/services';
import {PatientService} from '@core/services/patient.service';
import {HTTPMethods, ModalTypes} from '@core/enums';
import {ATC} from '@core/classes/other/atc';
import {HttpHeaders} from '@angular/common/http';
import {DefaultSearchComponent} from '@shared/components/dropdown/search/default-search/default-search.component';
import {lastValueFrom} from 'rxjs';
import {Response, ResponseSingle} from '@core/classes/other/response';
import {MedicalDevice} from '@core/classes/tables/medical-device';
import {Pregnancy} from '@core/classes/tables/pregnancy';
import {checkDate} from '@core/helpers';

@Component({
  selector: 'ncp-add-pregnancy',
  imports: [
    ButtonComponent,
    DateComponent,
    DropdownComponent,
    FormsModule,
    ModalComponent,
    NgOptimizedImage,
    ReactiveFormsModule
  ],
  templateUrl: './add-pregnancy.component.html',
  styleUrl: './add-pregnancy.component.scss',
})
export class AddPregnancyComponent implements OnInit, AfterViewInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('phFrm') protected pregnanciesFormEl?: NgForm;
  @ViewChild('addPregnancyButton') public addPregnancyButton!: ButtonComponent;

  @ViewChild( 'pregnancyDesc') public pregnancyDesc!: DropdownComponent<Coding>;
  @ViewChild('pregnancyDate') public pregnancyDate!: DateComponent;

  protected readonly toastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private apiService = inject(APIService);
  private patientService = inject(PatientService);
  private dataService = inject(DataService);

  protected data = model<any>();

  private _params = {};
  protected form: FormGroup = new FormGroup({
    coding_id: new FormControl<number | undefined>(undefined, [Validators.required]),
    deliveryDate: new FormControl<string>('', [Validators.required]),
  }, {
    validators: [this.formValidator()]
  });

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let codingId = (control as FormGroup).controls['coding_id'];
      let deliveryDate = (control as FormGroup).controls['deliveryDate'];

      if(codingId.value === undefined || codingId.value === null) {
        codingId.setErrors({dt_codingIdInvalid: true});
        errors['ph_codingIdInvalid'] = true;
      }

      if(!checkDate(deliveryDate.value).isValid()) {
        deliveryDate.setErrors({dt_dateFormatInvalid: true});
        errors['ph_deliveryDateInvalid'] = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  protected phLoading: boolean = false;

  public btnPHOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'phBtn',
      tooltip: 'add_pregnancy',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      title: 'add_pregnancy',
      type: ModalTypes.form,
      form: FormComponent,
      options: {
        animation: true,
        size: 'xl',
        fullscreen: "lg",
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {this.onFormReset()}
    })
  );

  protected pregnancyDescDDOptions = signal<DropdownOptions<Coding | ATC>>(
    new DropdownOptions<Coding | ATC>({
      id: 'pregnancyDescription',
      name: 'pregnancyDescription',
      placeholder: 'pregnancy_description',
      label: {
        id: "pregnancy_desc_label",
        text: 'pregnancy_description',
      },
      api: {
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.9',
          description: ''
        },
      },
      required: true,
      searchComponent: DefaultSearchComponent,
      formControl: this.form.get('coding_id') as FormControl
    })
  );

  protected pregnancyDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'deliveryDate',
      label: {
        text: 'delivery_date',
      },
      name: 'deliveryDate',
      initialValue: '',
      required: true,
      formControl: this.form.get('deliveryDate') as FormControl
    })
  );

  ngOnInit() {
    this.btnPHOptions().onClick = (event) => {
      this.mod.options().onReset = this.onFormReset;
      this.pregnancyDesc.detectChanges();
      this.addPregnancyButton.blur();
      this.phLoading = true;
      this.downloadDropDownData(this.mod.options().title).then((value) => {
        this.phLoading = false;
        this.changeDetectorRef.detectChanges();
        this.mod.open();
      });
    };

    //this.pregnancyDateOptions().formControl = (this.form.get('deliveryDate') as FormControl);
    this.modOptions().onSave = this.onSave;
    this.modOptions().onShow = this.downloadDropDownData;
  }

  onFormReset = () => {
    console.log('on reset form');
    this.form.reset();
    this.pregnancyDesc.clearSelection();
    this.pregnancyDate.reset();

    this.modOptions().errors = [];
  }

  ngAfterViewInit(): void {

  }

  protected downloadDropDownData = async (title?: string) => {
    let ddData = JSON.parse(localStorage.getItem('dropDownData')!) as Object || {};

    if(ddData.hasOwnProperty('pregnancyDescription')) {
      return;
    }

    const data = await lastValueFrom(this.apiService.call({
      path: '/get-all_codings',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      body: {
        oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.9',
        description: ''
      }
    })!);

    if (data && data.status === 1 && data.data.length > 0) {
      Object.defineProperty(ddData, 'pregnancyDescription', {
        value: (data as Response<Coding>).data,
        enumerable: true,
        writable: false
      });
      this.pregnancyDesc.data = (data as Response<Coding>).data || [];

      localStorage.setItem('dropDownData', JSON.stringify(ddData));
    }
  }

  onSave = () => {
    this.pregnanciesFormEl?.ngSubmit.emit();
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      this.form.controls[control].markAsTouched();
    })

    return true;
  }

  onFormSubmit = () => {
    console.log(this.form.value);
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;
    let body = {
      expected_date_of_delivery: (this.form.get('deliveryDate') as FormControl).value,
      coding_id: this.pregnancyDescDDOptions().value!.coding_id,
      status: 1,
      version_id: this.patientService.version!.new,
    }

    this.apiService.call({
      path: '/pregnancy',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      type: HTTPMethods.POST,
      body: body
    })?.subscribe((res: ResponseSingle<Pregnancy>) => {
      if(res && res.status === 1) {
        let data = this.data()?.tblOptions().data;
        data.unshift(res.data);
        if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
          this.data()!.tbl!.tableData! = data;
        }
        this.mod.close();
      }
    })
  }

  protected readonly assets = assets;
}
